import React, { useState, useEffect, useCallback } from 'react';
import { CheckSquare, Square, ClipboardList, RefreshCw, ExternalLink } from 'lucide-react';
import ApiService from '../services/ApiService';

const AllTasksView = ({ onViewWorkOrder }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'all' | 'pending' | 'completed'
  const [assigneeFilter, setAssigneeFilter] = useState('');

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter === 'pending') params.set('completed', 'false');
      if (filter === 'completed') params.set('completed', 'true');
      if (assigneeFilter.trim()) params.set('assignedTo', assigneeFilter.trim());
      const data = await ApiService.apiCall(`/workordertask/all?${params.toString()}`);
      setTasks(data || []);
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, assigneeFilter]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const toggleTask = async (task) => {
    try {
      const updated = await ApiService.apiCall(`/workordertask/${task.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: task.title,
          description: task.description,
          dueDate: task.dueDate,
          assignedTo: task.assignedTo,
          completed: !task.completed
        })
      });
      setTasks(prev => {
        const next = prev.map(t => t.id === task.id ? updated : t);
        // If filtering, remove tasks that no longer match
        if (filter === 'pending' && updated.completed) return next.filter(t => t.id !== task.id);
        if (filter === 'completed' && !updated.completed) return next.filter(t => t.id !== task.id);
        return next;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const isOverdue = (task) =>
    !task.completed && task.dueDate && new Date(task.dueDate) < new Date();

  const pendingCount = tasks.filter(t => !t.completed).length;
  const overdueCount = tasks.filter(t => isOverdue(t)).length;

  // Group by work order
  const grouped = tasks.reduce((acc, task) => {
    const key = task.workOrderId;
    if (!acc[key]) acc[key] = { workOrderNumber: task.workOrderNumber, clientName: task.clientName, workOrderId: task.workOrderId, tasks: [] };
    acc[key].tasks.push(task);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Header + stats */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-800">{pendingCount}</p>
            <p className="text-xs text-gray-500">Pending</p>
          </div>
          {overdueCount > 0 && (
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{overdueCount}</p>
              <p className="text-xs text-red-500">Overdue</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Status filter */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            {['all', 'pending', 'completed'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 capitalize transition-colors ${filter === f ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Assignee filter */}
          <input
            type="text"
            placeholder="Filter by assignee"
            value={assigneeFilter}
            onChange={e => setAssigneeFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm w-40"
          />

          <button onClick={loadTasks} className="p-1.5 border border-gray-200 rounded-lg hover:bg-gray-50" title="Refresh">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Task list */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-8">Loading tasks...</p>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No tasks found.</p>
          <p className="text-xs mt-1">Tasks are added from within work orders.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.values(grouped).map(group => (
            <div key={group.workOrderId} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Work order header */}
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-800">{group.workOrderNumber}</span>
                  {group.clientName && (
                    <span className="text-xs text-gray-500">{group.clientName}</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    {group.tasks.filter(t => t.completed).length}/{group.tasks.length} done
                  </span>
                  {onViewWorkOrder && (
                    <button
                      onClick={() => onViewWorkOrder(group.workOrderId)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                      title="View Work Order"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View
                    </button>
                  )}
                </div>
              </div>

              {/* Tasks */}
              <div className="divide-y divide-gray-100">
                {group.tasks.map(task => (
                  <div
                    key={task.id}
                    className={`flex items-start gap-3 px-4 py-3 ${task.completed ? 'opacity-60' : ''}`}
                  >
                    <button
                      onClick={() => toggleTask(task)}
                      className={`mt-0.5 flex-shrink-0 ${task.completed ? 'text-green-500' : 'text-gray-400 hover:text-blue-500'}`}
                    >
                      {task.completed ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${task.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                        {task.title}
                      </p>
                      {task.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                      )}
                      <div className="flex gap-3 mt-1 flex-wrap">
                        {task.dueDate && (
                          <span className={`text-xs ${isOverdue(task) ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                            Due: {task.dueDate.split('T')[0]}{isOverdue(task) ? ' — Overdue' : ''}
                          </span>
                        )}
                        {task.assignedTo && (
                          <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                            {task.assignedTo}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllTasksView;
